import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';

// ---------------------------------------------------------------------------
// ProgressBar – horizontal progress indicator with ARIA support
// ---------------------------------------------------------------------------

/**
 * Color theme options for the progress bar
 *
 * The 9-name decorative palette stays in the type for API back-compat
 * with the existing tio-employee + tio-admin consumers. At render time
 * each name resolves to the TIO canonical 6-recipe palette via the
 * `.app-progress-bar-fill--<name>` CSS class family — `teal`, `blue`,
 * and `indigo` all share the `info` recipe; `emerald` and `green` share
 * `success`; and so on (see `addons/tio-ui/src/styles/`).
 */
export type ProgressBarColor =
  'emerald' | 'blue' | 'violet' | 'amber' | 'teal' | 'red' | 'green' | 'indigo' | 'gray';

/**
 * Size options for the progress bar height
 */
export type ProgressBarSize = 'xs' | 'sm' | 'md' | 'lg';

interface ProgressBarSignature {
  Args: {
    /** Current value (0 to max). Defaults to 0 if undefined. */
    value?: number;
    /** Maximum value (default: 100) */
    max?: number;
    /** Color theme */
    color?: ProgressBarColor;
    /** Size variant */
    size?: ProgressBarSize;
    /** Use gradient fill instead of solid */
    gradient?: boolean;
    /** Custom aria-label */
    label?: string;
  };
  Element: HTMLDivElement;
}

/**
 * ProgressBar - A horizontal progress bar with ARIA support
 *
 * Displays a progress indicator with customizable color, size, and
 * optional gradient fill. Fully accessible with ARIA progressbar role.
 *
 * Styling lives in the `.app-progress-bar` CSS class family — see
 * `addons/tio-ui/src/styles/`. The component itself only
 * computes the percent + inline width style + accessibility attributes.
 *
 * @example Basic usage
 * ```gts
 * import ProgressBar from 'tio-ui/components/progress-bar';
 *
 * <ProgressBar @value={{75}} @color="emerald" />
 * ```
 *
 * @example With custom max and size
 * ```gts
 * <ProgressBar @value={{42}} @max={{100}} @color="violet" @size="lg" />
 * ```
 *
 * @example Gradient style
 * ```gts
 * <ProgressBar @value={{60}} @gradient={{true}} @color="blue" />
 * ```
 */
export default class ProgressBar extends Component<ProgressBarSignature> {
  get max() {
    const max = this.args.max ?? 100;
    return max > 0 ? max : 100;
  }

  get percent() {
    const value = Math.max(0, Math.min(this.args.value ?? 0, this.max));
    return Math.round((value / this.max) * 100);
  }

  get sizeClass() {
    return `app-progress-bar--${this.args.size ?? 'sm'}`;
  }

  get fillClass() {
    const color = this.args.color ?? 'blue';
    const gradient = this.args.gradient ? ' app-progress-bar-fill--gradient' : '';
    return `app-progress-bar-fill app-progress-bar-fill--${color}${gradient}`;
  }

  get ariaLabel() {
    return this.args.label ?? `${this.percent}% complete`;
  }

  <template>
    <div
      data-test-reimagined-progress-bar
      class="app-progress-bar {{this.sizeClass}}"
      role="progressbar"
      aria-valuenow={{this.percent}}
      aria-valuemin={{0}}
      aria-valuemax={{100}}
      aria-label={{this.ariaLabel}}
      ...attributes
    >
      <div
        data-test-reimagined-progress-bar-fill
        class={{this.fillClass}}
        {{style width=(concat this.percent "%")}}
      ></div>
    </div>
  </template>
}

export type { ProgressBarSignature };
